/*
 * Copyright The Adaptive Palette copyright holders
 * See the AUTHORS.md file at the top-level directory of this distribution and at
 * https://github.com/inclusive-design/adaptive-palette/raw/main/AUTHORS.md.
 *
 * Licensed under the New BSD license. You may not use this file except in
 * compliance with this License.
 *
 * You may obtain a copy of the License at
 * https://github.com/inclusive-design/adaptive-palette/blob/main/LICENSE
 */

/**
 * The attributes set on the message being composed: what the message is for, how it should
 * sound, how the user feels, and how urgent it is.
 *
 * They are message-level: they belong to the one message and clear with it. They are held apart
 * from the message itself so they never reach speech or the message log as content.
 */
import { signal } from "@preact/signals";
import type { MessageAttributeType } from "../../index.d";
import { resolveLabel, t, type StringKey } from "../../i18n/I18n";

/**
 * The order the categories are reported to the model in. It matches the row order of
 * `public/palette-sets/standardBlissChart/palettes/attributes.json`, so the prompt reads the way
 * the palette looks. A category not listed here is still reported, after these, in the order it
 * was selected — but only these four are placed to match the palette; adding a palette row in a
 * new category means adding it here too if it should be reported in position.
 */
const CATEGORY_ORDER = ["Intent", "Tone", "Feeling", "Priority"];

// The name shown for each category. The palette and the prompt keep the English one.
const CATEGORY_KEYS: Record<string, StringKey> = {
  Intent: "attributeIntent", Tone: "attributeTone", Feeling: "attributeFeeling", Priority: "attributePriority"
};

/**
 * A category's name in the current language. A category with no translation is shown as
 * the palette has it.
 * @param {string} category - The category as the palette names it.
 * @returns {string}
 */
export function categoryName (category: string): string {
  return Object.hasOwn(CATEGORY_KEYS, category) ? t(CATEGORY_KEYS[category]) : category;
}

/**
 * An attribute's label in the current language.
 * @param {MessageAttributeType} attribute - The attribute.
 * @returns {string}
 */
export function attributeLabel (attribute: MessageAttributeType): string {
  return resolveLabel(attribute.label, "en").text;
}

/**
 * Nothing selected. Shared rather than rewritten at each use, so `clearAttributes()` on an
 * already-empty selection does not publish a new array and wake every reader. Frozen so a
 * consumer mutating what it reads (e.g. `.push()`) cannot poison this shared empty state.
 */
const NO_ATTRIBUTES: readonly MessageAttributeType[] = Object.freeze([]);

/**
 * The attributes currently set, in the order they were selected. Read freely; write through
 * `toggleAttribute()` and `clearAttributes()`.
 */
export const selectedAttributesSignal = signal<readonly MessageAttributeType[]>(NO_ATTRIBUTES);

/**
 * Whether two attributes are the same one. Compared by category and symbol, not label: the
 * label changes with the UI language. Not by identity either: the cell builds a fresh object
 * on every render.
 * @param {MessageAttributeType} first - One attribute.
 * @param {MessageAttributeType} second - The other.
 * @returns {boolean}
 */
function isSameAttribute (first: MessageAttributeType, second: MessageAttributeType): boolean {
  return first.category === second.category &&
    JSON.stringify(first.composition) === JSON.stringify(second.composition);
}

/**
 * Whether an attribute is currently set.
 * @param {MessageAttributeType} attribute - The attribute to look for.
 * @returns {boolean}
 */
export function isAttributeSelected (attribute: MessageAttributeType): boolean {
  return selectedAttributesSignal.value.some((selected) => isSameAttribute(selected, attribute));
}

/**
 * Set an attribute that is not set, or unset one that is.
 * @param {MessageAttributeType} attribute - The attribute the user tapped.
 * @returns {void}
 */
export function toggleAttribute (attribute: MessageAttributeType): void {
  const selected = selectedAttributesSignal.peek();
  const without = selected.filter((existing) => !isSameAttribute(existing, attribute));
  selectedAttributesSignal.value = without.length === selected.length
    ? [...selected, attribute]
    : without;
}

/**
 * Unset every attribute. Called wherever the message is discarded.
 * @returns {void}
 */
export function clearAttributes (): void {
  if (selectedAttributesSignal.peek().length > 0) {
    selectedAttributesSignal.value = NO_ATTRIBUTES;
  }
}

/**
 * The attributes as one line for a prompt: categories in palette order, separated by
 * semicolons, with several attributes in one category separated by commas. For example
 * `Intent: question; Feeling: angry`. Empty when nothing is selected, which is what lets
 * `renderPromptLines()` drop the line.
 *
 * Reads the signal rather than peeking it, so an effect that calls this is subscribed to it.
 * @returns {string}
 */
export function attributesPromptText (): string {
  const selected = selectedAttributesSignal.value;
  const rank = (category: string): number => {
    const index = CATEGORY_ORDER.indexOf(category);
    return index === -1 ? CATEGORY_ORDER.length : index;
  };
  return [...new Set(selected.map((attribute) => attribute.category))]
    .sort((first, second) => rank(first) - rank(second))
    .map((category) => {
      const labels = selected
        .filter((attribute) => attribute.category === category)
        .map(attributeLabel)
        .join(", ");
      return `${category}: ${labels}`;
    })
    .join("; ");
}
